import { useEffect, useState } from 'react';

import { Trans } from '@lingui/react/macro';
import { Close, Edit } from '@mui/icons-material';
import { Button } from '@repo/videa-components/src/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from '@repo/videa-components/src/ui/dialog';
import { Textarea } from '@repo/videa-components/src/ui/textarea';
import classNames from 'classnames';

import { PreferenceButton } from '../../../common-components/composed/preferenceButton/PreferenceButton';
import { useAdultTreatmentOptions } from '../../../modules/annotator/casePresentation/patientDownloadable/TreatmentOptions';
import { usePediatricTreatmentOptions } from '../../../modules/annotator/patientEducationDialog/PediatricTabs';
import {
  usePreferences,
  usePreferencesOrgUpdate,
} from '../../../react-query/usePreferences';

export const PatientEducationTxCopyDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [customCopy, setCustomCopy] = useState<Record<string, string>>({});

  const { data: preferences } = usePreferences();
  const defaultTreatments = useAdultTreatmentOptions(true);
  const pediatricTreatments = usePediatricTreatmentOptions(true);

  const treatments = {
    ...defaultTreatments,
    ...pediatricTreatments,
  };

  const { mutate: updateOrgPreference } = usePreferencesOrgUpdate();

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);

  // Initialize with current values when dialog opens
  useEffect(() => {
    if (isOpen) {
      const currentValues: Record<string, string> = {};

      (Object.keys(treatments) as (keyof typeof treatments)[]).forEach(
        (key) => {
          // Use custom copy from preferences if available, otherwise use default
          const customValue = preferences?.customPatientEducationTxCopy?.[key];
          const defaultValue = treatments[key]?.description || '';
          currentValues[key] = customValue || defaultValue;
        },
      );

      // Only update if values actually changed
      setCustomCopy((prev) => {
        const isSame = Object.keys(currentValues).every(
          (key) => prev[key] === currentValues[key],
        );
        return isSame ? prev : currentValues;
      });
    }
    // Only depend on isOpen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSave = () => {
    const prev = (preferences?.customPatientEducationTxCopy || {}) as Record<
      string,
      string
    >;
    const changed: Record<string, string> = {};

    Object.entries(customCopy).forEach(([key, value]) => {
      // only include if the value is NOT default
      if (
        value !==
        defaultTreatments[key as keyof typeof defaultTreatments]?.description
      ) {
        if (value !== '' || prev[key]) {
          changed[key] = value;
        }
      }
      // if it is default, set to empty string
      else {
        changed[key] = '';
      }
    });

    if (Object.keys(changed).length > 0) {
      updateOrgPreference({ customPatientEducationTxCopy: changed });
    }
  };

  const handleTreatmentChange = (treatmentKey: string, value: string) => {
    setCustomCopy((prev) => ({
      ...prev,
      [treatmentKey]: value,
    }));
  };

  // Compute if there are any changes
  const prev = (preferences?.customPatientEducationTxCopy || {}) as Record<
    string,
    string
  >;
  const hasChanges = (
    Object.keys(treatments) as (keyof typeof treatments)[]
  ).some((key) => {
    const current = customCopy[key] || '';
    const prevValue = prev[key] || treatments[key]?.description || '';
    return current !== prevValue;
  });

  // Validation: check for empty values
  const emptyKeys = (
    Object.keys(treatments) as (keyof typeof treatments)[]
  ).filter((key) => (customCopy[key] || '').trim() === '');
  const hasEmpty = emptyKeys.length > 0;

  return (
    <>
      <PreferenceButton
        onClick={handleOpen}
        className="pref-button"
        startIcon={<Edit />}
      >
        <Trans>Treatment Descriptions for Patient Education</Trans>
      </PreferenceButton>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent
          hideClose
          className="vai-max-w-[800px] vai-w-[90vw] vai-max-h-[90vh] vai-z-[1400] vai-overflow-y-auto"
        >
          <DialogHeader>
            <div className="vai-flex vai-flex-row vai-justify-start vai-items-center">
              <DialogClose className="vai-rounded-sm vai-bg-[#eaeff6] vai-p-2 vai-flex vai-items-center vai-justify-center">
                <Close className="vai-text-grey-500" />
              </DialogClose>
              <span className="vai-text-foreground-mutedText vai-font-neue-haas vai-font-normal vai-text-2xl vai-ml-2">
                <Trans>Treatment Descriptions for Patient Education</Trans>
              </span>
            </div>
          </DialogHeader>

          <div className="vai-flex vai-flex-col vai-gap-8 vai-pb-2">
            <span className="vai-text-blue-black-630 vai-font-neue-haas vai-font-normal vai-text-md">
              <Trans>
                Customize the descriptions shown to patients for each treatment
                type
              </Trans>
            </span>

            {(Object.keys(treatments) as (keyof typeof treatments)[]).map(
              (key) => {
                const label = treatments[key]?.label || '';
                const defaultValue = treatments[key]?.description || '';
                const isDefault = (customCopy[key] || '') === defaultValue;
                const isEmpty = (customCopy[key] || '').trim() === '';
                return (
                  <div
                    key={key}
                    className="vai-flex vai-flex-col vai-gap-2 vai-bg-white vai-shadow-md vai-rounded-xl vai-p-6 vai-border vai-border-grey-200"
                  >
                    <div className="vai-flex vai-items-center vai-gap-2">
                      <span className="vai-text-foreground-base vai-font-neue-haas vai-font-medium vai-text-md">
                        {label}
                      </span>
                      <Button
                        size="sm"
                        variant="sheet"
                        className={classNames('vai-ml-auto', {
                          '!vai-outline-none': isDefault,
                        })}
                        disabled={isDefault}
                        onClick={() => handleTreatmentChange(key, defaultValue)}
                      >
                        <Trans>Reset to Default</Trans>
                      </Button>
                    </div>
                    <Textarea
                      className={classNames(
                        'vai-w-full vai-bg-background-accent/50 vai-rounded-lg vai-p-4 vai-py-2 vai-text-base vai-min-h-[120px] vai-transition vai-duration-200 vai-outline-none vai-border-0 focus:vai-border focus:vai-border-sapphire-300 focus:vai-shadow-md hover:vai-border hover:vai-border-sapphire-300 hover:vai-shadow-md',
                        { 'vai-border vai-border-blaze-500': isEmpty },
                      )}
                      placeholder={`Enter custom description for ${label.toLowerCase()}...`}
                      value={customCopy[key] || ''}
                      onChange={(e) =>
                        handleTreatmentChange(key, e.target.value)
                      }
                      autoResize
                    />
                    {isEmpty && (
                      <span className="vai-text-blaze-500 vai-text-xs vai-mt-1">
                        <Trans>This field cannot be empty.</Trans>
                      </span>
                    )}
                  </div>
                );
              },
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button onClick={handleSave} disabled={!hasChanges || hasEmpty}>
              <Trans>Save Changes</Trans>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
